import { logger } from '../services/logger';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/db';
import { useAuth } from '../lib/auth';
import { ArrowLeft, Search, Send, Trash2 } from 'lucide-react';

interface Notification {
  id: string;
  created_at: string;
  user_id: string;
  title: string;
  message: string;
  is_read: boolean;
  user_email?: string;
}

interface User {
  id: string;
  email: string;
  full_name?: string;
}

export function AdminNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New notification form state
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is admin
        if (!user || user.app_metadata?.role !== 'admin') {
          navigate('/markets');
          return;
        }

        // Fetch all notifications with user information
        const { data: notificationData, error: notificationError } = await supabase
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false });

        if (notificationError) throw notificationError;

        // Get unique user IDs from notifications
        const userIds = [...new Set(notificationData.map((n: any) => n.user_id))];
        
        // Fetch user emails directly from auth.users
        const { data: authUsersData, error: authUsersError } = await supabase.auth.admin.listUsers({
          perPage: 1000
        });
        
        if (authUsersError) {
          logger.error('Error fetching user data:', authUsersError);
        }
        
        // Create a lookup map of user_id to email
        const userEmailMap: Record<string, string> = {};
        if (authUsersData?.users) {
          authUsersData.users.forEach((authUser: any) => {
            userEmailMap[authUser.id] = authUser.email;
          });
        }

        // Transform data to include user email
        const formattedNotifications = notificationData.map((notification: any) => ({
          ...notification,
          user_email: userEmailMap[notification.user_id] || null
        }));

        setNotifications(formattedNotifications);

        // Fetch all users for the dropdown (using profiles as before)
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, email, full_name');

        if (profilesError) throw profilesError;
        setUsers(profilesData);

      } catch (error) {
        logger.error('Error fetching data:', error);
        setError('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      setError('Title and message are required');
      return;
    }
    
    try {
      setSending(true);
      
      if (selectedUserId === 'all') {
        // Send to all users
        const notifications = users.map(user => ({
          user_id: user.id,
          title: notificationTitle,
          message: notificationMessage,
          is_read: false
        }));
        
        const { error } = await supabase
          .from('notifications')
          .insert(notifications);
          
        if (error) throw error;
      } else {
        // Send to specific user
        const { error } = await supabase
          .from('notifications')
          .insert({
            user_id: selectedUserId,
            title: notificationTitle,
            message: notificationMessage,
            is_read: false
          });
          
        if (error) throw error;
      }
      
      // Reset form
      setNotificationTitle('');
      setNotificationMessage('');
      setSelectedUserId('all');
      
      // Refresh notifications
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;

      // Get unique user IDs from notifications
      const refreshUserIds = [...new Set(data.map((n: any) => n.user_id))];
      
      // Fetch user emails directly from auth.users
      const { data: authUsersData, error: authUsersError } = await supabase.auth.admin.listUsers({
        perPage: 1000
      });
      
      if (authUsersError) {
        logger.error('Error fetching user data:', authUsersError);
      }
      
      // Create a lookup map of user_id to email
      const refreshUserEmailMap: Record<string, string> = {};
      if (authUsersData?.users) {
        authUsersData.users.forEach((authUser: any) => {
          refreshUserEmailMap[authUser.id] = authUser.email;
        });
      }
      
      const formattedNotifications = data.map((notification: any) => ({
        ...notification,
        user_email: refreshUserEmailMap[notification.user_id] || null
      }));
      
      setNotifications(formattedNotifications);
      
    } catch (error) {
      logger.error('Error sending notification:', error);
      setError('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);
        
      if (error) throw error;
      
      // Update local state
      setNotifications(notifications.filter(n => n.id !== notificationId));
      
    } catch (error) {
      logger.error('Error deleting notification:', error);
      setError('Failed to delete notification');
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = searchTerm
    ? notifications.filter(notification =>
        notification.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : notifications;

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">

      <div className="flex items-center gap-3 mb-8">
        <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold">Notifications</h1>
      </div>
      
      {/* Send New Notification Form */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Send Notification</h2>
        <form onSubmit={handleSendNotification}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-wayja-teal focus:border-wayja-teal sm:text-sm rounded-md"
              >
                <option value="all">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-wayja-teal focus:border-wayja-teal sm:text-sm"
                placeholder="Notification Title"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-wayja-teal focus:border-wayja-teal sm:text-sm"
                placeholder="Notification message..."
                required
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-wayja-teal hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wayja-teal"
            >
              {sending ? (
                <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </form>
        {error && (
          <div className="mt-4 text-red-600 text-sm">{error}</div>
        )}
      </div>
      
      {/* Notification List */}
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search notifications"
          className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredNotifications.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No notifications found
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => (
              <div key={notification.id} className="p-6 flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-grow mr-4">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{notification.title}</h3>
                    {notification.is_read ? (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full">Read</span>
                    ) : (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">Unread</span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-2">{notification.message}</p>
                  <div className="flex text-sm text-gray-500">
                    <span className="mr-4">To: {notification.user_email || notification.user_id}</span>
                    <span>Sent: {new Date(notification.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  <button
                    onClick={() => handleDeleteNotification(notification.id)}
                    className="inline-flex items-center p-2 border border-transparent rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    title="Delete notification"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 